// Missions et récompenses à l'écran : prochaine mission, progression (niveau, points) et badges.

import { levelOf, MISSIONS, missionStatus, nextMission, type MissionDef, type MissionsState } from '../../store/missions';
import { href } from '../router';

export function missionHref(def: MissionDef): string {
  return def.suggestedMode === 'tempo' ? href('pratique', def.suggestedScoreId, 'tempo') : href('pratique', def.suggestedScoreId);
}

function StatusChip({ status }: { status: 'todo' | 'discovery' | 'instrument' }) {
  if (status === 'instrument') return <span className="chip passed">Réussie sur instrument</span>;
  if (status === 'discovery') return <span className="chip started">Faite en découverte</span>;
  return <span className="chip">À faire</span>;
}

/** Carte « Ma prochaine mission » de l'accueil. */
export function NextMissionCard({ state }: { state: MissionsState }) {
  const def = nextMission(state);
  if (!def) {
    return (
      <article className="action-card mission">
        <p className="eyebrow">Ma prochaine mission</p>
        <h2>Toutes les missions sont réussies</h2>
        <p className="muted">De nouvelles missions arriveront avec le catalogue élargi. En attendant, rejouez vos morceaux au tempo cible.</p>
        <a className="btn primary" href={href('catalogue')}>
          Voir le catalogue
        </a>
      </article>
    );
  }
  const status = missionStatus(state, def.id);
  return (
    <article className="action-card mission">
      <p className="eyebrow">Ma prochaine mission</p>
      <h2>
        <span className="mission-icon" aria-hidden="true">
          {def.icon}
        </span>
        {def.title}
      </h2>
      <p className="goal">{def.goal}</p>
      <p className="reward-line">
        <span className="xp-pill">+{def.xp.instrument} XP</span>
        <span className="muted">badge « {def.badge.instrument} »</span>
        {!def.instrumentOnly && <span className="muted">· découverte au clavier d’essai : +{def.xp.discovery} XP</span>}
      </p>
      {status === 'discovery' && <p className="muted small">Déjà faite en découverte : refaites-la sur votre piano (micro ou MIDI) pour le badge complet.</p>}
      <a className="btn primary" href={missionHref(def)}>
        Relever la mission
      </a>
    </article>
  );
}

/** Progression : niveau, points, badges et état des missions. */
export function RewardsPanel({ state }: { state: MissionsState }) {
  const lvl = levelOf(state.xp);
  return (
    <section className="panel rewards">
      <div className="rewards-head">
        <h2>Mes récompenses</h2>
        <span className="level-pill">Niveau {lvl.level}</span>
      </div>
      <div className="xp-bar" role="progressbar" aria-valuemin={0} aria-valuemax={lvl.next} aria-valuenow={lvl.into} aria-label={`${state.xp} points d’expérience`}>
        <span className="xp-fill" style={{ width: `${Math.min(100, (lvl.into / lvl.next) * 100)}%` }} />
      </div>
      <p className="muted small">
        {state.xp} XP au total · {lvl.into} / {lvl.next} vers le niveau {lvl.level + 1}
      </p>
      <ul className="mission-list">
        {MISSIONS.map((m) => {
          const status = missionStatus(state, m.id);
          return (
            <li key={m.id} className={status === 'todo' ? 'mission-item' : `mission-item ${status}`}>
              <span className="mission-icon" aria-hidden="true">
                {m.icon}
              </span>
              <div className="mission-body">
                <strong>{m.title}</strong>
                <span className="muted small">{m.goal}</span>
              </div>
              <StatusChip status={status} />
            </li>
          );
        })}
      </ul>
      <details className="details">
        <summary>Conditions et récompenses</summary>
        <ul>
          {MISSIONS.map((m) => (
            <li key={m.id}>
              <strong>{m.title}</strong> : {m.detail} Récompense : {m.instrumentOnly ? `${m.xp.instrument} XP et le badge « ${m.badge.instrument} »` : `${m.xp.discovery} XP en découverte (clavier d’essai, synthèse) ou ${m.xp.instrument} XP et le badge « ${m.badge.instrument} » sur instrument`}.
            </li>
          ))}
          <li>Chaque badge n’est attribué qu’une fois. Les points sont conservés sur cet appareil, dans ce navigateur. Une détection incertaine n’est jamais comptée comme juste ni comme fausse.</li>
        </ul>
      </details>
    </section>
  );
}
